// DS: PO24
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal, { MODAL_FOOTER_BUTTON_SIZE } from '../Modal';
import TextInput from '../../../formControls/dsPresets/TextInput';
import TextButton from '../../../Buttons/TextButton';
import BrandedButton from '../../../Buttons/BrandedButton';
import s from './PromptModal.scss';

export interface Props {
  isOpen?: boolean;
  title: string;
  inputLabel?: string;
  inputPlaceholder?: string;
  initialInputValue?: string;
  inputWrapperClassName?: string;
  doneButtonLabel?: string;
  minLength?: number;
  maxLength?: number;
  extrasBeforeInput?: React.ReactNode;
  confirmDisabled?: boolean;
  onInputValidate?: (inputValue?: string) => string | undefined;
  onCancel?: () => void;
  onDone?: (inputValue?: string) => void;
}

export const PromptModal: React.FC<Props> = ({
  initialInputValue = '',
  isOpen,
  title,
  inputLabel,
  inputPlaceholder,
  doneButtonLabel,
  inputWrapperClassName,
  extrasBeforeInput,
  maxLength,
  minLength,
  confirmDisabled: confirmDisabledProp,
  onInputValidate,
  onCancel,
  onDone,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(initialInputValue);
  const [confirmState, setConfirmState] = useState('ready');
  const [cancelState, setCancelState] = useState('ready');

  const errorHint =
    inputValue !== initialInputValue && onInputValidate
      ? onInputValidate(inputValue)
      : undefined;

  const onInputKeyUp = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (!errorHint && ev.key === 'Enter' && !confirmDisabled) {
      onDoneHandler();
    }
  };

  const onDoneHandler = () => onDone && onDone(inputValue);

  const onClickConfirm = () => {
    setConfirmState('busy');
    setCancelState('disabled');
    onDoneHandler();
  };

  const confirmDisabled =
    !!errorHint ||
    confirmDisabledProp ||
    (minLength ? inputValue.length < minLength : false) ||
    confirmState === 'disabled';
  const confirmLoading = confirmState === 'busy';

  return (
    <Modal
      className={s['root']}
      isOpen={isOpen ?? false}
      onRequestClose={() => onCancel && onCancel()}
      heading={title}
      footerRight={[
        <TextButton
          key="cancel-btn"
          onClick={() => {
            setConfirmState('disabled');
            setCancelState('busy');
            onCancel && onCancel();
          }}
          size={MODAL_FOOTER_BUTTON_SIZE}
          variant="main"
          loading={cancelState === 'busy'}
          disabled={cancelState === 'disabled'}
        >
          {t('popup.cancel')}
        </TextButton>,
        <BrandedButton
          key="confirm-btn"
          size={MODAL_FOOTER_BUTTON_SIZE}
          variant="brand-green"
          disabled={confirmDisabled}
          onClick={onClickConfirm}
          loading={confirmLoading}
        >
          {doneButtonLabel || t('popup.done')}
        </BrandedButton>,
      ]}
    >
      <div className={s['content-wrapper']}>
        {extrasBeforeInput}
        <div className={inputWrapperClassName}>
          <TextInput
            id="prompt-modal-input"
            value={inputValue}
            placeholder={inputPlaceholder}
            label={inputLabel || ''}
            validationResult={errorHint ? 'error' : undefined}
            details={errorHint}
            onChange={ev => setInputValue(ev.target.value)}
            onKeyUp={onInputKeyUp}
            maxLength={maxLength}
            isLabelHidden={!inputLabel}
            autoFocus
          />
        </div>
      </div>
    </Modal>
  );
};
PromptModal.displayName = 'PromptModal';

export default PromptModal;
